import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";

import { Form } from "antd";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Table,
  MerchantFilters,
} from "../../shared";
import { BusinessModal } from "../../components";
import UpdateLeadModal from "../RateReview/components/Modals/UpdateLeadModal/UpdateLeadModal";

import {
  fetchMerchantData,
  formatMerchantData,
  getStatusClass,
} from "../../helpers/merchantHelpers";
import { searchLeadsCombined } from "../../services/zohoService";
import { updateRateReviewLead, processLeadUpdateValues } from "../RateReview/services/rateReviewApiService";
import { mapLeadToFormValues } from "../RateReview/utils/leadFormatters";
import { hasPermission } from "../../utils/roleHelpers";
import { DEBOUNCE_CONFIG, DEFAULT_PAGE_SIZE } from "../../utils/constants";
import { useMessageApi } from "../../shared/providers/MessageProvider";
import "./MerchantListView.scss";

/** Statuses for which UpdateLeadModal opens instead of BusinessModal (with rate_review edit permission) */
const RATE_REVIEW_MODAL_STATUSES = [
  "Review Info Received",
  "Review Being Analyzed",
  "Calculating Savings",
  "Schedule a Call to discuss",
];

/**
 * MerchantListView Component with Modern Filters
 * --------------------------
 * Displays a list of merchants with modern filtering and original table structure.
 *
 * Props:
 * - userInfo: user details from parent (used for default filter)
 */
const MerchantListView = ({userInfo, onCompanyChange, onCsvExportDataUpdate, permissions }) => {
  // Use Auth0 React SDK hook
  const { isAuthenticated } = useAuth0();
  
  // Message API from MessageProvider for toast notifications
  const message = useMessageApi();
  
  const isMountedRef = useRef(true);

  // ------------------------------------------
  // State variables to manage UI and data
  // ------------------------------------------
  // Modal display state
  const [displayModal, setDisplayModal] = useState(false);
  const [modalMerchant, setModalMerchant] = useState(null);
  // UpdateLeadModal (for Rate Review status leads)
  const [isUpdateLeadModalVisible, setIsUpdateLeadModalVisible] = useState(false);
  const [selectedLeadForUpdate, setSelectedLeadForUpdate] = useState(null);
  const [isUpdatingLead, setIsUpdatingLead] = useState(false);
  const [saveTick, setSaveTick] = useState(0);
  const [updateLeadForm] = Form.useForm();
  
  // Loading states
  const [isLoading, setIsLoading] = useState(true);

  // Data states
  const [merchants, setMerchants] = useState([]);
  
  // Ref to track previous company to avoid duplicate API calls
  const prevCompanyRef = useRef(null);
  // Ref to track if initialization has been completed
  const isInitializedRef = useRef(false);

  // Original filter states
  const [currentFilters, setCurrentFilters] = useState({});
  
  // Infinite scroll controls
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Sorting state
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [secondarySortColumn, setSecondarySortColumn] = useState(null);
  const [secondarySortDirection, setSecondarySortDirection] = useState(null);

  // Modern filter states for new UI
  const [modernFilters, setModernFilters] = useState({
    businessName: "",
    gatewayId: "",
    submittedDateRange: { start: null, end: null, preset: null, label: null },
    approvalDateRange: { start: null, end: null, preset: null, label: null },
    lastUpdatedDateRange: { start: null, end: null, preset: null, label: null },
    status: [],
    salesRep: ""
  });

  // Combined search states
  const [combinedSearchResults, setCombinedSearchResults] = useState([]);
  const [isCombinedSearching, setIsCombinedSearching] = useState(false);
  const [showCombinedResults, setShowCombinedResults] = useState(false);

  // Individual filter loading states - using single object
  const [filterLoadingStates, setFilterLoadingStates] = useState({
    businessName: false,
    gatewayId: false,
    submittedDate: false,
    approvalDate: false,
    lastUpdatedDate: false,
    status: false
  });

  // Helper functions for managing loading states
  const setFilterLoading = useCallback((filterName, isLoading) => {
    setFilterLoadingStates(prev => ({ ...prev, [filterName]: isLoading }));
  }, []);

  const clearAllFilterLoading = useCallback(() => {
    setFilterLoadingStates({
      businessName: false,
      gatewayId: false,
      submittedDate: false,
      approvalDate: false,
      lastUpdatedDate: false,
      status: false
    });
  }, []);

  // Track when any filter has returned empty results to prevent API calls on scroll
  const hasEmptyResults = useMemo(() => {
    // Check if combined search mode is active but has empty results
    if (showCombinedResults && combinedSearchResults.length === 0 && !isCombinedSearching) {
      return true;
    }
    
    // Check if regular merchant list is empty and not loading
    if (!showCombinedResults && merchants.length === 0 && !isLoading) {
      return true;
    }
    
    return false;
  }, [
    showCombinedResults,
    combinedSearchResults.length,
    isCombinedSearching,
    merchants.length,
    isLoading
  ]);

  // Define table headers based on company
  const headers = useMemo(() => {
    const baseHeaders = ["Business Name"];
    
    // Add Sales Rep and Account Ex based on company
    if (currentFilters.company === "CyberSource") {
      baseHeaders.push("Account Ex");
    } else {
      // For all other companies (Authorize.net, FreedomMerchant, Vb360, etc.), show Sales Rep
      baseHeaders.push("Sales Rep");
    }
    
    baseHeaders.push("Email", "GW ID", "Date Submitted", "Date Approved", "Updated Date", "Status");
    
    return baseHeaders;
  }, [currentFilters.company]);

  // Mount/Cleanup effect - ensure ref is set correctly on mount and unmount
  useEffect(() => {
    // Set mounted to true on mount (handles React Strict Mode remounts)
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
      clearInterval(window.progressInterval);
      if (individualFilterTimerRef.current) {
        clearTimeout(individualFilterTimerRef.current);
      }
      if (combinedSearchTimerRef.current) {
        clearTimeout(combinedSearchTimerRef.current);
      }
    };
  }, []);

  const effectivePermissions = useMemo(
    () => permissions || userInfo?.permissions || {},
    [permissions, userInfo]
  );

  const canEditAllLeads = useMemo(
    () => effectivePermissions?.dashboard?.edit_leads === true,
    [effectivePermissions]
  );

  const canEditRateReview = useMemo(
    () => hasPermission(userInfo, "rate_review_portal", "edit"),
    [userInfo]
  );

  /** Returns whether the current user can edit this lead (edit all OR lead assigned to user) */
  const canEditThisLead = useCallback(
    (merchant) => {
      if (canEditAllLeads) return true;
      if (!userInfo || !merchant) return false;
      const normalize = (v) =>
        typeof v === "string" ? v.trim().toLowerCase() : "";
      const ids = [
        userInfo?.name,
        userInfo?.email,
        userInfo?.full_name,
        userInfo?.fullName,
        userInfo?.display_name,
        userInfo?.displayName,
      ]
        .map(normalize)
        .filter(Boolean);
      const owners = [
        merchant?.Sales_Agent,
        merchant?.Account_Executive,
        merchant?.Account_Rep,
        merchant?.Submitting_Agent,
      ]
        .map(normalize)
        .filter(Boolean);
      return ids.some((id) => owners.includes(id));
    },
    [canEditAllLeads, userInfo]
  );

  /**
   * Load merchants data from API with infinite scroll support
   */
  const loadData = useCallback(
    async (page, rows, filters, showLoader = true, isAppending = false) => {
      if (!isMountedRef.current) return;

      // Show appropriate loading UI based on context
      if (showLoader) {
        if (isAppending) {
          setIsLoadingMore(true);
        } else {
          setIsLoading(true);
        }
      }

      try {
        const { list, info } = await fetchMerchantData(page, rows, filters);

        if (!isMountedRef.current) return;

        if (isAppending) {
          // Append new data to existing merchants
          setMerchants(prev => [...prev, ...list.map(formatMerchantData)]);
        } else {
          // Replace merchants data (initial load or filter change)
        setMerchants(list.map(formatMerchantData));
        }

        setHasMore(info.more_records);
      } catch (error) {
        if (!isMountedRef.current) return;
        message.error("Failed to load merchant data", 3);
        console.error("Merchant load error:", error);
      } finally {
        if (!isMountedRef.current) return;

        if (showLoader) {
          if (isAppending) {
            setIsLoadingMore(false);
          } else {
            setIsLoading(false);
          }
        }
      }
    },
    [isMountedRef, message]
  );

  /**
   * Initial setup effect
   */
  useEffect(() => {
    if (!isAuthenticated || !userInfo || isInitializedRef.current) return;

    const initialize = async () => {
      setIsLoading(true);
      try {
        const initialCompany = userInfo.company;
        const filters = { company: initialCompany };

        setCurrentFilters(filters);
        prevCompanyRef.current = initialCompany; // Initialize ref with initial company
        isInitializedRef.current = true; // Mark as initialized
        if (onCompanyChange) onCompanyChange(initialCompany);

        await loadData(1, DEFAULT_PAGE_SIZE, filters);
      } catch (err) {
        message.error("Initialization failed", 3);
        console.error("Init error:", err);
      } finally {
        if (isMountedRef.current) setIsLoading(false);
      }
    };

    initialize();
  }, [userInfo?.email, loadData, onCompanyChange, message]);

  // -------------------- Event Handlers --------------------

  // Debounce timer refs
  const individualFilterTimerRef = useRef(null);
  const combinedSearchTimerRef = useRef(null);

  /**
   * Clear all sorting
   */
  const clearSorting = useCallback(() => {
    setSortColumn(null);
    setSortDirection(null);
    setSecondarySortColumn(null);
    setSecondarySortDirection(null);
  }, []);

  /**
   * Memoized search parameters builder for performance optimization
   */
  const buildSearchParams = useCallback((filters) => {
    const searchParams = {};

    // Add business name filter if provided
    if (filters.businessName?.trim()) {
      searchParams.businessName = filters.businessName.trim();
    }

    // Add gateway ID filter if provided
    if (filters.gatewayId?.trim()) {
      searchParams.gatewayId = filters.gatewayId.trim();
    }

    // Add status filter if provided
    if (filters.status && filters.status.length > 0) {
      searchParams.status = filters.status;
    }

    // Add sales rep filter if provided
    if (filters.salesRep?.trim()) {
      searchParams.salesRep = filters.salesRep.trim();
    }

    // Add date range filters as separate objects
    if (filters.submittedDateRange) {
      searchParams.submittedDateRange = filters.submittedDateRange;
    }
    
    if (filters.approvalDateRange) {
      searchParams.approvalDateRange = filters.approvalDateRange;
    }
    
    if (filters.lastUpdatedDateRange) {
      searchParams.lastUpdatedDateRange = filters.lastUpdatedDateRange;
    }

    return searchParams;
  }, []);

  /**
   * Search function that supports individual and multiple filters
   * Uses the combined search API endpoint
   */
  const searchLeadsHandler = useCallback(async (filters, company) => {
    if (!company) {
      setCombinedSearchResults([]);
      setShowCombinedResults(false);
      return;
    }

    setIsCombinedSearching(true);
    try {
      // Prepare search parameters using memoized function
      const searchParams = {
        company,
        ...buildSearchParams(filters)
      };

      const result = await searchLeadsCombined(searchParams);
      
      if (result?.success && result?.data) {
        // Format the results to match the table structure
        const formattedResults = result.data.map(lead => ({
          // Keep all original raw data for modal
          ...lead,
          // Add formatted display fields for table
          id: lead.id,
          businessName: lead.Company || 'N/A',
          salesRep: lead.Sales_Agent || 'N/A',
          accountEx: lead.Account_Executive || 'N/A',
          email: lead.Email || 'N/A',
          gwId: lead.Gateway_ID || 'N/A',
          dateSubmitted: lead.Created_Time ? new Date(lead.Created_Time).toLocaleDateString() : 'N/A',
          dateApproved: lead.Lead_Status === 'Approved' ? (lead.Date_Approved ? new Date(lead.Date_Approved).toLocaleDateString() : 'N/A') : 'N/A',
          dateUpdated: lead.Modified_Time ? new Date(lead.Modified_Time).toLocaleDateString() : 'N/A',
          status: lead.Lead_Status || 'N/A',
          statusClass: getStatusClass(lead.Lead_Status)
        }));
        
        setCombinedSearchResults(formattedResults);
        setShowCombinedResults(true);
        
        // Set hasMore to false since search-combined doesn't support pagination
        setHasMore(false);
      } else {
        setCombinedSearchResults([]);
        setShowCombinedResults(true);
        setHasMore(false);
      }
    } catch (error) {
      console.error('Combined search error:', error);
      setCombinedSearchResults([]);
      setShowCombinedResults(false);
      setHasMore(false);
      message.error('Failed to search with combined filters', 3);
    } finally {
      setIsCombinedSearching(false);
      // Clear all individual loading states
      clearAllFilterLoading();
    }
  }, [buildSearchParams, message, clearAllFilterLoading]);

  /**
   * Build search filters from modernFilters
   */
  const buildSearchFilters = useCallback((inputFilters = modernFilters) => {
    const filters = {};

    // Add business name filter
    if (inputFilters.businessName?.trim()) {
      filters.businessName = inputFilters.businessName.trim();
    }

    // Add gateway ID filter
    if (inputFilters.gatewayId?.trim()) {
      filters.gatewayId = inputFilters.gatewayId.trim();
    }

    // Add status filter
    if (inputFilters.status && inputFilters.status.length > 0) {
      filters.status = inputFilters.status;
    }

    // Add sales rep filter
    if (inputFilters.salesRep?.trim()) {
      filters.salesRep = inputFilters.salesRep.trim();
    }

    // Add date range filters as separate objects
    // Process Submitted Date Range
    if (inputFilters.submittedDateRange?.start && inputFilters.submittedDateRange?.end) {
      filters.submittedDateRange = {
        startDate: inputFilters.submittedDateRange.start,
        endDate: inputFilters.submittedDateRange.end,
        dateField: 'submitted'
      };
    } else if (inputFilters.submittedDateRange?.preset) {
      filters.submittedDateRange = {
        rangeType: inputFilters.submittedDateRange.preset,
        dateField: 'submitted'
      };
    }

    // Process Approval Date Range
    if (inputFilters.approvalDateRange?.start && inputFilters.approvalDateRange?.end) {
      filters.approvalDateRange = {
        startDate: inputFilters.approvalDateRange.start,
        endDate: inputFilters.approvalDateRange.end,
        dateField: 'approval'
      };
    } else if (inputFilters.approvalDateRange?.preset) {
      filters.approvalDateRange = {
        rangeType: inputFilters.approvalDateRange.preset,
        dateField: 'approval'
      };
    }

    // Process Last Updated Date Range
    if (inputFilters.lastUpdatedDateRange?.start && inputFilters.lastUpdatedDateRange?.end) {
      filters.lastUpdatedDateRange = {
        startDate: inputFilters.lastUpdatedDateRange.start,
        endDate: inputFilters.lastUpdatedDateRange.end,
        dateField: 'lastUpdated'
      };
    } else if (inputFilters.lastUpdatedDateRange?.preset) {
      filters.lastUpdatedDateRange = {
        rangeType: inputFilters.lastUpdatedDateRange.preset,
        dateField: 'lastUpdated'
      };
    }

    return filters;
  }, [modernFilters]);

  const hasActiveModernFilters = useMemo(() => {
    const filters = buildSearchFilters(modernFilters);
    return Boolean(
      filters.businessName ||
      filters.gatewayId ||
      (filters.status && filters.status.length > 0) ||
      filters.salesRep ||
      filters.submittedDateRange ||
      filters.approvalDateRange ||
      filters.lastUpdatedDateRange
    );
  }, [modernFilters, buildSearchFilters]);

  /**
   * Check if any filter is active and trigger search using combined API
   */
  const checkAndTriggerSearch = useCallback((filters) => {
    // Build search filters from the passed filters parameter
    const searchFilters = buildSearchFilters(filters);
    
    const activeFilters = [];
    
    if (searchFilters.businessName?.trim()) activeFilters.push('businessName');
    if (searchFilters.gatewayId?.trim()) activeFilters.push('gatewayId');
    if (searchFilters.status && searchFilters.status.length > 0) activeFilters.push('status');
    if (searchFilters.salesRep?.trim()) activeFilters.push('salesRep');
    if (searchFilters.submittedDateRange || searchFilters.approvalDateRange || searchFilters.lastUpdatedDateRange) {
      activeFilters.push('dateRange');
    }
    
    // If any filter is active, use combined search
    if (activeFilters.length >= 1) {
      // Set loading state immediately to prevent flash of old data
      setIsCombinedSearching(true);
      
      // Clear existing timer
      if (combinedSearchTimerRef.current) {
        clearTimeout(combinedSearchTimerRef.current);
      }
      
      // Set new timer for debounced search
      combinedSearchTimerRef.current = setTimeout(() => {
        // Clear combined search results
        setCombinedSearchResults([]);
        setShowCombinedResults(false);
        
        // Reset pagination for new search
        setCurrentPage(1);
        
        // Trigger search using combined API
        const combinedFilters = {
          company: currentFilters.company || userInfo?.company,
          ...searchFilters
        };
        searchLeadsHandler(combinedFilters, currentFilters.company);
      }, DEBOUNCE_CONFIG.COMBINED_SEARCH); // Optimized debounce for combined search
      
      return true;
      } else {
      // No filters are active - return to regular merchant list
      // Set loading state immediately to prevent flash of old data
      setIsLoading(true);
      
      // Clear existing timer
      if (combinedSearchTimerRef.current) {
        clearTimeout(combinedSearchTimerRef.current);
      }
      
      // Set new timer for debounced return to regular list
      combinedSearchTimerRef.current = setTimeout(() => {
        // Clear combined search results
        setCombinedSearchResults([]);
        setShowCombinedResults(false);
        setHasMore(false);
        
        // Reset pagination and reload regular merchant data
        setCurrentPage(1);
        loadData(1, rowsPerPage, currentFilters);
      }, DEBOUNCE_CONFIG.COMBINED_SEARCH); // Use same debounce for consistency
      
      return true;
    }
  }, [currentFilters.company, searchLeadsHandler, userInfo?.company, loadData, rowsPerPage, buildSearchFilters]);

  /**
   * Get preset label for display
   */
  const getPresetLabel = (preset) => {
    const labels = {
      'today': 'Today',
      'yesterday': 'Yesterday',
      'week_to_date': 'Week To Date',
      'month_to_date': 'Month To Date',
      'year_to_date': 'Year To Date',
      'last_week': 'Last Week',
      'last_month': 'Last Month',
      'last_year': 'Last Year'
    };
    return labels[preset] || preset;
  };

  /**
   * Load more data for infinite scroll
   */
  const loadMoreData = useCallback(() => {
    // Prevent API calls if any filter has returned empty results
    if (hasEmptyResults) {
      return;
    }

    if (hasMore && !isLoadingMore && !isLoading) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      
      // Check if we're in combined search mode
      if (showCombinedResults) {
        // Load more combined search results
        const filters = buildSearchFilters();
        searchLeadsHandler(nextPage, rowsPerPage, filters, true);
      } else {
        // Regular pagination for normal merchant list
        loadData(nextPage, rowsPerPage, currentFilters, true, true);
      }
    }
  }, [hasMore, isLoadingMore, isLoading, hasEmptyResults, currentPage, rowsPerPage, currentFilters, loadData, showCombinedResults, searchLeadsHandler, buildSearchFilters]);

  /**
   * Clear individual filter handlers - using single object
   */
  const clearHandlers = useMemo(() => {
    const createClearHandler = (filterName, clearValue) => () => {
      const updatedFilters = { ...modernFilters, [filterName]: clearValue };
      setModernFilters(updatedFilters);
      
      // Check if other filters are still active and trigger search
      setTimeout(() => {
        checkAndTriggerSearch(updatedFilters);
      }, 100); // Small delay to ensure state is updated
    };

    return {
      businessName: createClearHandler('businessName', ''),
      gatewayId: createClearHandler('gatewayId', ''),
      submittedDate: createClearHandler('submittedDateRange', { start: null, end: null, preset: null, label: null }),
      approvalDate: createClearHandler('approvalDateRange', { start: null, end: null, preset: null, label: null }),
      lastUpdatedDate: createClearHandler('lastUpdatedDateRange', { start: null, end: null, preset: null, label: null }),
      status: createClearHandler('status', [])
    };
  }, [modernFilters, checkAndTriggerSearch]);

  /**
   * Clear all filters and reload data with default filters (company only)
   */
  const handleClearAllFilters = useCallback(() => {
    // Reset all modern filters to default state
    setModernFilters({
      businessName: "",
      gatewayId: "",
      submittedDateRange: { start: null, end: null, preset: null, label: null },
      approvalDateRange: { start: null, end: null, preset: null, label: null },
      lastUpdatedDateRange: { start: null, end: null, preset: null, label: null },
      status: [],
      salesRep: ""
    });
    
    // Clear combined search results and reset search states
    setCombinedSearchResults([]);
    setShowCombinedResults(false);
    
    // Reset to default filters (just company)
    const defaultFilters = {
      company: currentFilters.company || userInfo?.company
    };
    
    setCurrentFilters(defaultFilters);
    setCurrentPage(1);
    setHasMore(false); // Reset hasMore to allow new data loading
    clearSorting();
    
    // Reload data with default filters - this will reset empty results state
    loadData(1, rowsPerPage, defaultFilters);
  }, [currentFilters.company, userInfo?.company, rowsPerPage, loadData, clearSorting]);

  // Modern filter handlers
  const handleModernFilterChange = useCallback((filterName, value) => {
    // Check if this is a preset selection
    if (value?.preset && value?.rangeType) {
      // Get the preset label immediately
      const presetLabel = getPresetLabel(value.preset);
      
      // Set loading state immediately to prevent flash of old data
      setIsCombinedSearching(true);
      
      // Set loading state based on date range type
      if (value.rangeType === 'submittedDateRange') {
        setFilterLoading('submittedDate', true);
      } else if (value.rangeType === 'approvalDateRange') {
        setFilterLoading('approvalDate', true);
      } else if (value.rangeType === 'lastUpdatedDateRange') {
        setFilterLoading('lastUpdatedDate', true);
      }
      
      // Create updated filters with the new preset
      const updatedFilters = {
        ...modernFilters,
        [value.rangeType]: { 
        start: null, 
        end: null, 
        preset: value.preset, 
        label: presetLabel 
        }
      };
      
      setModernFilters(updatedFilters);
      
      // Clear combined search results
      setCombinedSearchResults([]);
      setShowCombinedResults(false);
      
      // Trigger combined search
      checkAndTriggerSearch(updatedFilters);
      return;
    }
    
    // Always preserve existing filters and only update the current one
    const updatedFilters = { ...modernFilters, [filterName]: value };
    
    setModernFilters(updatedFilters);
    
    // Handle Gateway ID search with debounce
    if (filterName === 'gatewayId') {
      // Set loading state immediately to prevent flash of old data
      if (value.trim() && currentFilters.company) {
        setFilterLoading('gatewayId', true);
        setIsCombinedSearching(true);
      } else {
        setIsLoading(true);
      }
      
      // Clear existing timer
      if (individualFilterTimerRef.current) {
        clearTimeout(individualFilterTimerRef.current);
      }
      
      // Set new timer for debounced search
      individualFilterTimerRef.current = setTimeout(() => {
        if (value.trim() && currentFilters.company) {
          // Clear combined search results to show new search
          setCombinedSearchResults([]);
          setShowCombinedResults(false);
          
          // Reset pagination for new search
          setCurrentPage(1);
          
          // Trigger combined search
          checkAndTriggerSearch(updatedFilters);
        } else {
          // Clear combined search results and return to regular merchant list
          setCombinedSearchResults([]);
          setShowCombinedResults(false);
          setHasMore(false);
          // Reset pagination and reload regular merchant data when Gateway search is cleared
          setCurrentPage(1);
          loadData(1, rowsPerPage, currentFilters);
        }
      }, DEBOUNCE_CONFIG.TEXT_INPUT); // Optimized debounce for Gateway ID search
      
      return; // Don't proceed with normal filter logic for Gateway ID
    }

    // Handle general search with debounce
    if (filterName === 'businessName') {
      // Set loading state immediately to prevent flash of old data
      if (value.trim() && currentFilters.company) {
        setFilterLoading('businessName', true);
        setIsCombinedSearching(true);
      } else {
        setIsLoading(true);
      }
      
      // Clear existing timer
      if (individualFilterTimerRef.current) {
        clearTimeout(individualFilterTimerRef.current);
      }
      
      // Set new timer for debounced search
      individualFilterTimerRef.current = setTimeout(() => {
        if (value.trim() && currentFilters.company) {
          // Clear combined search results to show new search
          setCombinedSearchResults([]);
          setShowCombinedResults(false);
          
          // Reset pagination for new search
          setCurrentPage(1);
          
          // Trigger combined search
          checkAndTriggerSearch(updatedFilters);
        } else {
          // Clear combined search results and return to regular merchant list
          setCombinedSearchResults([]);
          setShowCombinedResults(false);
          setHasMore(false);
          // Reset pagination and reload regular merchant data when search is cleared
          setCurrentPage(1);
          loadData(1, rowsPerPage, currentFilters);
        }
      }, DEBOUNCE_CONFIG.TEXT_INPUT); // Optimized debounce for Business Name search
      
      return; // Don't proceed with normal filter logic for businessName
    }

    // Handle custom date range filters (manual calendar selection)
    if (filterName === 'submittedDateRange' || filterName === 'approvalDateRange' || filterName === 'lastUpdatedDateRange') {
      // Only trigger search if both start and end dates are selected
      const hasCompleteRange = value.start && value.end;
      
      if (hasCompleteRange) {
        // Set loading state immediately to prevent flash of old data
        setIsCombinedSearching(true);
        
        // Set loading state based on date range type
        if (filterName === 'submittedDateRange') {
          setFilterLoading('submittedDate', true);
        } else if (filterName === 'approvalDateRange') {
          setFilterLoading('approvalDate', true);
        } else if (filterName === 'lastUpdatedDateRange') {
          setFilterLoading('lastUpdatedDate', true);
        }
        
        // Clear combined search results to show new search
        setCombinedSearchResults([]);
        setShowCombinedResults(false);
        
        // Clear existing custom date range timer
        if (individualFilterTimerRef.current) {
          clearTimeout(individualFilterTimerRef.current);
        }
        
        // Set debounced timer for custom date range search
        individualFilterTimerRef.current = setTimeout(() => {
          // Trigger combined search
          checkAndTriggerSearch(updatedFilters);
        }, DEBOUNCE_CONFIG.DATE_RANGE); // Optimized debounce for date range selection
      } else {
        // If clearing or incomplete range, just update the filter
        // Clear combined search results when date ranges change
        if (showCombinedResults) {
          setCombinedSearchResults([]);
          setShowCombinedResults(false);
        }
        // Don't call any API when date range is incomplete - just update the filter state
      }
      
      return;
    }
    
    // Check if this is an incomplete date range - if so, don't call API
    const isIncompleteDateRange = (filterName === 'submittedDateRange' || filterName === 'approvalDateRange' || filterName === 'lastUpdatedDateRange') &&
                                  value && value.start && !value.end;
    
    if (isIncompleteDateRange) {
      // Just update the filter state, don't call any API
      setModernFilters(updatedFilters);
      return;
    }
    
    // Convert modern filters to original format and apply
    const convertedFilters = {
      ...currentFilters,
      company: currentFilters.company || userInfo?.company
    };
    
    setCurrentFilters(convertedFilters);
    setCurrentPage(1);
    clearSorting();
    loadData(1, rowsPerPage, convertedFilters);
  }, [currentFilters, userInfo?.company, rowsPerPage, loadData, clearSorting, modernFilters]);

  const handleModernStatusChange = useCallback((status, checked, newStatuses = null) => {
    let newStatus;
    let isSelectAll = false;
    
    if (status === "SELECT_ALL") {
      // Handle Select All case
      if (checked) {
        // When Select All is checked, we want to show all statuses as selected on frontend
        // but send empty array to backend (no status filter)
        newStatus = newStatuses || [];
        isSelectAll = true;
      } else {
        // When Select All is unchecked, clear all selections
        newStatus = [];
      }
    } else {
      // Handle individual status selection
      newStatus = checked 
        ? [...modernFilters.status, status]
        : modernFilters.status.filter(s => s !== status);
    }
    
    // Create updated filters with the new status
    const updatedFilters = {
      ...modernFilters,
        status: newStatus
    };
    
    setModernFilters(updatedFilters);
    
    // Set loading state immediately to prevent flash of old data
    setFilterLoading('status', true);
    
    // Check if other filters are active to determine which loading state to set
    const searchFilters = buildSearchFilters(updatedFilters);
    const hasOtherActiveFilters = 
      searchFilters.businessName?.trim() || 
      searchFilters.gatewayId?.trim() || 
      searchFilters.salesRep?.trim() ||
      searchFilters.submittedDateRange || 
      searchFilters.approvalDateRange || 
      searchFilters.lastUpdatedDateRange;
    
    // Set appropriate loading state immediately
    if (hasOtherActiveFilters || (searchFilters.status && searchFilters.status.length > 0)) {
      setIsCombinedSearching(true);
    } else {
      setIsLoading(true);
    }
    
    // Clear search results when status filter changes
    if (showCombinedResults) {
      setCombinedSearchResults([]);
      setShowCombinedResults(false);
    }
    
    // Clear existing status search timer
    if (individualFilterTimerRef.current) {
      clearTimeout(individualFilterTimerRef.current);
    }
    
    // Set new timer for debounced status search
    individualFilterTimerRef.current = setTimeout(() => {
      // Check if we should use combined search or individual search
      if (checkAndTriggerSearch(updatedFilters)) {
        return; // Combined search will be triggered
      }
      
      // Use individual status search if no other filters are active
      const company = currentFilters.company || userInfo?.company;
      
      if (!company) return;
      
      const statusFilters = {
        company: company
      };
      
      // For Select All: don't add status filter (empty array means no filter)
      // For individual selections: add status filter only if there are selected statuses
      if (newStatus.length > 0 && !isSelectAll) {
        statusFilters.status = newStatus;
      }
      // When isSelectAll is true, we intentionally don't add status filter
      
      // Update state first
      setCurrentFilters(statusFilters);
      setCurrentPage(1);
      clearSorting();
      
      // Call loadData directly with the new filters using ref
      loadData(1, rowsPerPage, statusFilters);
    }, DEBOUNCE_CONFIG.STATUS_SELECT); // Optimized debounce for status selection
  }, [modernFilters.status, currentFilters, userInfo?.company, rowsPerPage, clearSorting, showCombinedResults, userInfo, checkAndTriggerSearch, setFilterLoading, buildSearchFilters]);

  // Handle company changes from parent (Dashboard/Navbar)
  useEffect(() => {    
    // Only run if component is initialized and company actually changed
    if (isInitializedRef.current && userInfo?.company && userInfo.company !== prevCompanyRef.current) {
      // Clear combined search results when company changes
      if (showCombinedResults) {
        setCombinedSearchResults([]);
        setShowCombinedResults(false);
      }
      
      // Reset all modern filters when company changes
      setModernFilters({
        businessName: "",
        gatewayId: "",
        submittedDateRange: { start: null, end: null, preset: null, label: null },
        approvalDateRange: { start: null, end: null, preset: null, label: null },
        lastUpdatedDateRange: { start: null, end: null, preset: null, label: null },
        status: [],
        salesRep: ""
      });
      
      prevCompanyRef.current = userInfo.company;
      const companyFilters = { company: userInfo.company };
      setCurrentFilters(companyFilters);
      setCurrentPage(1);
      clearSorting();
      loadData(1, rowsPerPage, companyFilters);
    }
  }, [userInfo?.company, loadData, clearSorting, rowsPerPage]);

  // Browser scroll effect for infinite loading
  useEffect(() => {
    const handleScroll = () => {
      // Check if we're near the bottom of the page
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const threshold = 200; // Load more when 200px from bottom of page

      if (scrollTop + windowHeight >= documentHeight - threshold) {
        loadMoreData();
      }
    };

    // Add scroll listener to window instead of table container
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadMoreData]);

  /**
   * Handle business name click.
   * For Rate Review status leads → UpdateLeadModal (opens even without edit permission, modal handles view-only mode).
   * If user has no permission to edit this lead → show warning, don't open any modal.
   * Otherwise → BusinessModal.
   */
  const handleBusinessClick = useCallback(
    (merchant) => {
      if (document.activeElement) {
        document.activeElement.blur();
      }

      // Check if this is a Rate Review status lead first
      const status = merchant.Lead_Status || merchant.status || "";
      const isRateReviewStatus = RATE_REVIEW_MODAL_STATUSES.includes(status);

      // For Rate Review status leads, open UpdateLeadModal directly (skip permission check)
      if (isRateReviewStatus) {
        document.body.classList.add("modal-open");
        const formValues = mapLeadToFormValues(merchant);
        setSelectedLeadForUpdate({ ...merchant, ...formValues });
        updateLeadForm.setFieldsValue(formValues);
        setIsUpdateLeadModalVisible(true);
        return;
      }

      // Open BusinessModal for regular leads (view mode when no edit permission)
      document.body.classList.add("modal-open");
      setModalMerchant([merchant]);
      setDisplayModal(true);
    },
    [updateLeadForm, message]
  );

  /**
   * Handle table column sorting
   */
  const handleSort = useCallback((columnIndex) => {
    const header = headers[columnIndex];
    
    // Date Submitted column (now at index 4)
    if (columnIndex === 4) {
      let newDirection;
      if (sortColumn === columnIndex) {
        if (sortDirection === 'asc') {
          newDirection = 'desc';
        } else if (sortDirection === 'desc') {
          newDirection = null;
          setSortColumn(null);
          setSortDirection(null);
          return;
        } else {
          newDirection = 'asc';
        }
      } else {
        newDirection = 'asc';
      }
      
      setSortColumn(columnIndex);
      setSortDirection(newDirection);
    }
    
    // Date Approved column (now at index 5)
    if (columnIndex === 5) {
      let newDirection;
      if (secondarySortColumn === columnIndex) {
        if (secondarySortDirection === 'asc') {
          newDirection = 'desc';
        } else if (secondarySortDirection === 'desc') {
          newDirection = null;
          setSecondarySortColumn(null);
          setSecondarySortDirection(null);
          return;
        } else {
          newDirection = 'asc';
        }
      } else {
        newDirection = 'asc';
      }
      
      setSecondarySortColumn(columnIndex);
      setSecondarySortDirection(newDirection);
    }
  }, [headers, sortColumn, sortDirection, secondarySortColumn, secondarySortDirection]);

  /**
   * Handle modal outside click
   */
  const handleOutsideClick = useCallback(() => {
    document.body.classList.remove("modal-open");
    setDisplayModal(false);
    setModalMerchant(null);
  }, []);

  /**
   * Close UpdateLeadModal and reset state
   */
  const onUpdateLeadModalClose = useCallback(() => {
    document.body.classList.remove("modal-open");
    setIsUpdateLeadModalVisible(false);
    updateLeadForm.resetFields();
    setSelectedLeadForUpdate(null);
  }, [updateLeadForm]);

  /**
   * Refresh merchant data and update modal if open.
   * When merchantId is provided, returns the updated merchant from the refreshed list (for UpdateLeadModal keep-open flow).
   */
  const refreshInfo = useCallback(async (merchantId = null) => {
    try {
      // Reload the data first
      const { list } = await fetchMerchantData(currentPage, rowsPerPage, currentFilters);
      const formattedMerchants = list.map(formatMerchantData);
      
      // Update merchants state
      setMerchants(formattedMerchants);
      
      // If modal is open and we have a specific merchant ID, update the modal data
      if (displayModal && modalMerchant && merchantId) {
        // Find the updated merchant in the freshly loaded data
        const updatedMerchant = formattedMerchants.find(m => m.id === merchantId);
        if (updatedMerchant) {
          setModalMerchant([updatedMerchant]);
        }
      }

      // Return updated merchant when merchantId provided (e.g. for UpdateLeadModal to keep open with fresh data)
      if (merchantId) {
        return formattedMerchants.find(m => m.id === merchantId) ?? null;
      }
      return null;
    } catch (error) {
      console.error("Error refreshing merchant info:", error);
      return null;
    }
  }, [currentPage, rowsPerPage, currentFilters, displayModal, modalMerchant]);

  /**
   * Handle lead update from UpdateLeadModal (Rate Review). Respects rate_review_portal edit permission.
   * When options.keepOpen is true, modal stays open and selectedLead is refreshed with latest data.
   */
  const onUpdateLeadFinish = useCallback(
    async (formValues, options = {}) => {
      if (!selectedLeadForUpdate) return;
      const leadId = selectedLeadForUpdate.id || selectedLeadForUpdate.Id;
      if (!leadId) return;

      setIsUpdatingLead(true);
      try {
        const updates = processLeadUpdateValues(formValues);
        if (Object.keys(updates).length === 0) {
          message.warning("No changes to update", 2);
          setIsUpdatingLead(false);
          return;
        }
        await updateRateReviewLead({ leadId: String(leadId), updates });
        message.success("Lead updated successfully!", 3);
        setSaveTick((t) => t + 1);

        const keepOpen = options?.keepOpen === true;
        if (!keepOpen) {
          setIsUpdateLeadModalVisible(false);
          updateLeadForm.resetFields();
          setSelectedLeadForUpdate(null);
          document.body.classList.remove("modal-open");
        }

        const updatedMerchant = await refreshInfo(leadId);
        if (keepOpen && updatedMerchant) {
          const formValuesForLead = mapLeadToFormValues(updatedMerchant);
          setSelectedLeadForUpdate({ ...updatedMerchant, ...formValuesForLead });
        }
      } catch (error) {
        const errMsg =
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          "Failed to update lead";
        message.error(errMsg, 3);
      } finally {
        setIsUpdatingLead(false);
      }
    },
    [selectedLeadForUpdate, message, updateLeadForm, refreshInfo]
  );

  /**
   * Sort merchants based on current sort state
   */
  const sortedMerchants = useMemo(() => {

    // If showing combined search results, return them without sorting
    if (showCombinedResults) {
      return combinedSearchResults;
    }

    if (!sortColumn && !secondarySortColumn) {
      return merchants;
    }

    let sorted = [...merchants];

    if (!sortDirection && !secondarySortDirection) {
      return sorted;
    }

    sorted = sorted.sort((a, b) => {
      if (sortColumn === 4 && sortDirection) {
        const aValue = a.dateSubmitted || "";
        const bValue = b.dateSubmitted || "";

        if (!aValue && !bValue) {
        } else if (!aValue) {
          return 1;
        } else if (!bValue) {
          return -1;
        } else {
          const primaryResult = sortDirection === 'asc' 
            ? aValue.localeCompare(bValue) 
            : bValue.localeCompare(aValue);
          
          if (primaryResult !== 0) {
            if (secondarySortColumn === 5 && secondarySortDirection) {
              const aApproved = a.dateApproved || "";
              const bApproved = b.dateApproved || "";
              
              if (aApproved && bApproved) {
                const secondaryResult = secondarySortDirection === 'asc' 
                  ? aApproved.localeCompare(bApproved) 
                  : bApproved.localeCompare(aApproved);
                
                if (secondaryResult !== 0) {
                  return secondaryResult;
                }
              }
            }
            return primaryResult;
          }
        }
      }

      if (secondarySortColumn === 5 && secondarySortDirection) {
        const aValue = a.dateApproved || "";
        const bValue = b.dateApproved || "";

        if (!aValue && !bValue) return 0;
        if (!aValue) return 1;
        if (!bValue) return -1;

        return secondarySortDirection === 'asc' 
          ? aValue.localeCompare(bValue) 
          : bValue.localeCompare(aValue);
      }

      return 0;
    });

    return sorted;
  }, [merchants, sortColumn, sortDirection, secondarySortColumn, secondarySortDirection, headers, showCombinedResults, combinedSearchResults]);

  // Update CSV export data for Navbar whenever merchants or loading state changes
  useEffect(() => {
    if (onCsvExportDataUpdate) {
      onCsvExportDataUpdate({
        merchants: sortedMerchants,
        headers: headers,
        currentFilters: currentFilters,
        isLoading: isLoading
      });
    }
  }, [sortedMerchants, headers, currentFilters, isLoading, onCsvExportDataUpdate]);

  // If user is not authenticated, show nothing
  if (!isAuthenticated) return null;

  // Main component render
  return (
    <div className="modern-dashboard">
      {/* Filters Section */}
      <MerchantFilters
        modernFilters={modernFilters}
        onFilterChange={handleModernFilterChange}
        onStatusChange={handleModernStatusChange}
        company={currentFilters.company}
        filterLoadingStates={filterLoadingStates}
        clearHandlers={clearHandlers}
      />

      {/* Table Section - Full Width */}
      <section className="db-inner">
        <div className="container-fluid">
          <div className="row">
            {/* Full Width Table */}
            <div className="col-12 pt-3">
              <Table
                merchants={sortedMerchants}
                headers={headers}
                isLoading={isLoading || isCombinedSearching}
                onBusinessClick={handleBusinessClick}
                onSort={handleSort}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                secondarySortColumn={secondarySortColumn}
                secondarySortDirection={secondarySortDirection}
                rows={rowsPerPage}
                company={currentFilters.company}
                isLoadingMore={isLoadingMore}
                hasMore={hasMore}
                onLoadMore={loadMoreData}
                isMerchantListView={true}
                hideFooter={false}
                footerLoadingState={isLoading || isCombinedSearching}
                disableLoadMoreButton={hasActiveModernFilters}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Modal for detailed merchant info */}
      <BusinessModal
        handleOutsideClick={handleOutsideClick}
        display={displayModal}
        modalMerchant={modalMerchant}
        refreshInfo={refreshInfo}
        company={currentFilters.company}
        userInfo={userInfo}
        canEditAllLeads={canEditAllLeads}
      />

      {/* UpdateLeadModal for leads with Rate Review statuses (opens even without edit permission, modal handles view-only mode) */}
      <UpdateLeadModal
        open={isUpdateLeadModalVisible}
        onCancel={onUpdateLeadModalClose}
        selectedLead={selectedLeadForUpdate}
        form={updateLeadForm}
        onFinish={onUpdateLeadFinish}
        loading={isUpdatingLead}
        saveTick={saveTick}
        canEdit={canEditRateReview}
      />
    </div>
  );
};

export default React.memo(MerchantListView);